import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  useTheme,
  alpha,
  Fade,
} from "@mui/material";

import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";

/**
 * ConfirmDialog
 *
 * @param {boolean} open - Is dialog open
 * @param {function} onClose - Function to close dialog (Cancel)
 * @param {function} onConfirm - Function to run on confirmation
 * @param {string} title - Header text
 * @param {string} description - Body text
 * @param {'error' | 'warning' | 'info'} severity - Determines color/icon (Default: 'warning')
 * @param {string} confirmText - Text for the confirm button
 * @param {string} cancelText - Text for the cancel button
 */
const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title = "Are you sure?",
  description = "This action cannot be undone.",
  severity = "warning",
  confirmText = "Confirm",
  cancelText = "Cancel",
}) => {
  const theme = useTheme();

  // Configuration map for different severities
  const config = {
    error: {
      icon: <WarningAmberRoundedIcon sx={{ fontSize: 48 }} />,
      color: theme.palette.error.main,
      bgColor: alpha(theme.palette.error.main, 0.1),
      btnColor: "error", // Maps to MUI palette
    },
    warning: {
      icon: <WarningAmberRoundedIcon sx={{ fontSize: 48 }} />,
      color: theme.palette.warning.main,
      bgColor: alpha(theme.palette.warning.main, 0.1),
      btnColor: "warning",
    },
    info: {
      icon: <HelpOutlineOutlinedIcon sx={{ fontSize: 48 }} />,
      color: theme.palette.info.main,
      bgColor: alpha(theme.palette.info.main, 0.1),
      btnColor: "info",
    },
  };

  const currentConfig = config[severity] || config.warning;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Fade}
      PaperProps={{
        elevation: 24,
        sx: {
          borderRadius: theme.appRadius.lg,
          padding: 2,
          minWidth: 320,
          maxWidth: 450,
          textAlign: "center",
        },
      }}
    >
      {/* --- ICON HEADER --- */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 1,
          mb: 2,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            bgcolor: currentConfig.bgColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: currentConfig.color,
          }}
        >
          {currentConfig.icon}
        </Box>
      </Box>

      {/* --- CONTENT --- */}
      <DialogTitle sx={{ p: 0, mb: 1 }}>
        <Typography variant="h5" fontWeight={700}>
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0, mb: 3 }}>
        <Typography variant="body1" color="text.secondary">
          {description}
        </Typography>
      </DialogContent>

      {/* --- ACTIONS --- */}
      <DialogActions sx={{ justifyContent: "center", gap: 2, p: 0 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit" // Neutral gray
          sx={{
            borderRadius: theme.appRadius.md,
            px: 3,
            color: "text.secondary",
            borderColor: theme.palette.divider,
            "&:hover": {
              borderColor: "text.primary",
              bgcolor: "action.hover",
            },
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={() => {
            onConfirm();
            onClose();
          }}
          variant="contained" // Solid color is better for alerts than gradient sometimes, but we can use gradient if you prefer
          color={currentConfig.btnColor}
          autoFocus
          sx={{
            borderRadius: theme.appRadius.md,
            px: 3,
            fontWeight: 600,
            boxShadow: theme.shadows[4],
            // If you really want gradients here:
            // background: severity === 'error' ? theme.appGradients.red : undefined
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
